// src/oura/components/client/pageSentence.tsx
//
// The Client tab's page sentence: what needs us, what is late and what has a
// red flag, across the documents, the questions and the requests, each count
// said once. The cards under it say the rest (what waits on the client, what
// is not sent yet), so the sentence answers "what needs me, and what is
// stuck" and stops there.

import { Fragment, type ReactNode } from "react";
import type { BusinessDocuments } from "@service/aeo/clientDocs";
import { LATE_TEXT } from "@oura/ui";
import { agoText, plural, type DocsLoad } from "@oura/docs";
import { lateCodeRows } from "../docs/boardModel";
import type { AskGroups } from "./asks";
import type { RequestGroups } from "./changeRequests";

/** "a", "a and b", "a, b and c"; or "a, b or c". */
export function listText(parts: readonly string[], last: "and" | "or" = "and"): string {
    if (parts.length <= 1) return parts[0] ?? "";
    return `${parts.slice(0, -1).join(", ")} ${last} ${parts[parts.length - 1]}`;
}

/** The non-zero counts as words, and their sum for the verb. */
function counted(parts: Array<[number, string]>): { text: string; total: number } | null {
    const shown = parts.filter(([n]) => n > 0);
    if (shown.length === 0) return null;
    return { text: listText(shown.map(([n, noun]) => plural(n, noun))), total: shown.reduce((sum, [n]) => sum + n, 0) };
}

/**
 * The sentence, or null only when there is nothing at all to say. Clauses in
 * order, each only when it has something to say: a read that failed, the
 * folder's state, what needs us, what is late (in rose), what has a red flag.
 * With none of the last three, what waits on the client, or that nothing
 * needs us and nothing is late: only once every source was read, since an
 * unread one could hold what needs us.
 *
 * A folder Oura can no longer read keeps its last good counts; the sentence
 * ends by saying the doc counts are from then, so the live question and
 * request counts beside them are not read as old. Documents that are
 * unknown (loading, a failed read, a route not served yet, no folder, no
 * first read) add nothing: never a 0.
 */
export function clientSentence({
    docs,
    asks,
    requests,
    failedPart,
    othersRead,
}: {
    docs: Pick<DocsLoad<BusinessDocuments>, "status" | "data">;
    asks: AskGroups;
    requests: RequestGroups;
    failedPart: string | null;
    /** The questions, the calls and the change requests were all read: none failed or is not served yet. */
    othersRead: boolean;
}): ReactNode | null {
    const out: ReactNode[] = [];
    if (failedPart) out.push(failedPart);

    const board = docs.status === "ready" ? docs.data : null;
    const c = board?.folder ? board.counts : null;
    let stale: string | null = null;
    if (board) {
        if (!board.folder) out.push("The client's folder is not linked yet.");
        else if (!board.counts) out.push(board.folder.lastError ? "Oura could not read the client's folder." : "Oura is reading the client's folder.");
        else if (board.folder.lastError) {
            const ago = agoText(board.readAt);
            stale = `Oura could not read the client's folder, so the doc counts are from its last good read${ago ? `, ${ago}` : ""}.`;
        }
    }

    const needs = counted([
        [c?.needsUs ?? 0, "doc"],
        [asks.needsUs.length, "question"],
        [requests.needsUs.length, "request"],
    ]);
    if (needs) out.push(`${needs.text} ${needs.total === 1 ? "needs" : "need"} us.`);

    // A code not sent by its due date is late too (YW, 29 Sep 2026).
    const late = counted([
        [c?.late ?? 0, "doc"],
        [board && c ? lateCodeRows(board).length : 0, "code"],
        [asks.waiting.filter((a) => (a.item.lateDays ?? 0) > 0).length, "question"],
    ]);
    if (late) out.push(<span className={LATE_TEXT}>{`${late.text} ${late.total === 1 ? "is" : "are"} late.`}</span>);

    const flags = c?.redFlags ?? 0;
    if (flags > 0) out.push(`${plural(flags, "doc")} ${flags === 1 ? "has" : "have"} a red flag.`);

    // "Nothing needs us" only when nothing could: every source read.
    if (!needs && !late && flags === 0 && c && othersRead) {
        const waits = counted([
            [c.waitingOnClient, "doc"],
            [asks.waiting.length, "question"],
        ]);
        out.push(waits ? `Nothing needs us. ${waits.text} ${waits.total === 1 ? "waits" : "wait"} on the client.` : "Nothing needs us and nothing is late.");
    }
    if (stale) out.push(stale);

    if (out.length === 0) return null;
    return (
        <>
            {out.map((part, i) => (
                <Fragment key={i}>
                    {i > 0 && " "}
                    {part}
                </Fragment>
            ))}
        </>
    );
}
